import React, { useEffect, useState, useMemo, useContext } from "react";
import { useParams } from "react-router";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import DataTable from "react-data-table-component";
import Toast from "../../utils/Toast";

import Swal from "sweetalert2";
import useDeleteRoomType from "../../mutation/roomType/deleteRoomType";
import useCreateAudit from "../../mutation/audit/createAudit";

import useGetRoomTypeLinks from "../../queries/roomType/getRoomTypeLinks";
import useGetRoomTypes from "../../queries/roomType/getRoomTypes";
import { AuthContext } from "../../utils/userProvider";
import Loader from "../../components/Loader";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const RoomTypes = () => {
  const [linkedTable, setLinkedTable] = useState([]);
  const [rmsTable, setRmsTable] = useState([]);
  const [roomTypesTable, setRoomTypesTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);
  const [openDeleteRoomType, setOpenDeleteRoomType] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  const formatApartmentType = (value) => {
    if (!value) return "—";
    const v = String(value).toLowerCase();
    if (v === "shared") return "Shared";
    if (v === "studio") return "Studio";
    return value;
  };

  let columns = [
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "Display name",
      selector: "display_name",
      sortable: true,
      cell: (row) => <span>{row.display_name || "—"}</span>,
    },
    {
      name: "Apartment type",
      selector: "apartment_type",
      sortable: true,
      cell: (row) => <span>{formatApartmentType(row.apartment_type)}</span>,
    },
    {
      name: "Property",
      sortable: true,
      cell: (row, index, column, id) => {
        let property = row.property;
        return <span>{property?.name}</span>;
      },
    },
    {
      name: "Linked Suites",
      selector: "suites",
      cell: (row, index, column, id) => {
        let output = "";
        if (row.suites && row.suites.ids && roomTypesTable) {
          let suiteIds = row.suites.ids;
          for (var i = 0; i < suiteIds.length; i++) {
            if (i > 0) output += ", ";
            let suiteId = typeof suiteIds[i] === "number" ? suiteIds[i] : suiteIds[i];
            let found = roomTypesTable.find(
              (rt) => rt.id == suiteId || (rt.suites?.ids && Array.isArray(rt.suites.ids) && rt.suites.ids.includes(Number(suiteId)))
            );
            output += found ? `${found.display_name || found.name} (${suiteId})` : `Suite ID: ${suiteId}`;
          }
        } else if (row.wordpress && row.wordpress.ids && roomTypesTable) {
          let ids = row.wordpress.ids;
          for (var i = 0; i < ids.length; i++) {
            if (i > 0) output += ", ";
            let found = roomTypesTable.find((rt) => rt.id == ids[i]);
            output += found ? `${found.display_name || found.name} (${ids[i]})` : `ID: ${ids[i]}`;
          }
        }

        return <span>{output}</span>;
      },
    },
    {
      name: "Tags",
      selector: "tags",
      cell: (row, index, column, id) => {
        let output = "";
        if (row.tags) {
          let tags = row.tags;
          for (var i = 0; i < tags.length; i++) {
            if (i != 0) {
              output += ", ";
            }
            output += tags[i].name;
          }
        }

        return <span>{output}</span>;
      },
    },
  ];

  columns.push({
    name: "Options",
    sortable: false,
    cell: (row, index, column, id) => {
      if (authState.editAccess) {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CreateIcon />}
              onClick={() => {
                editItem(row);
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => {
                deleteItem(row);
              }}
            >
              Delete
            </Button>
          </div>
        );
      } else {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                editItem(row);
              }}
            >
              View
            </Button>
          </div>
        );
      }
    },
  });

  const [roomTypesResponse, resetRoomTypes] = useGetRoomTypes();
  const [linkResponse, resetLink] = useGetRoomTypeLinks();

  const [auditResponse, setAuditData] = useCreateAudit();

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = linkedTable.filter((item) => {
    const q = filterText.trim().toLowerCase();
    if (!q) return true;
    const name = (item.name || "").toLowerCase();
    const display = (item.display_name || "").toLowerCase();
    const apt = (item.apartment_type || "").toLowerCase();
    return name.includes(q) || display.includes(q) || apt.includes(q);
  });

  // useEffect(() => {
  //   if (response.data) {
  //     setRmsTable([...response.data]);
  //   }
  // }, [response]);

  useEffect(() => {
    if (roomTypesResponse.data) {
      setRoomTypesTable([...roomTypesResponse.data]);
    }
  }, [roomTypesResponse]);

  useEffect(() => {
    if (linkResponse.data) {
      console.log(linkResponse);
      setLinkedTable([...linkResponse.data]);
    }
  }, [linkResponse]);

  const editItem = (row) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/roomtypes/edit/" + row.id;
    } else {
      window.location.href = "/dashboard/roomtypes/view/" + row.id;
    }
  };

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeleteRoomType(true);
  };

  const handleClose = () => {
    setOpenDeleteRoomType(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "room-type",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Link deleted",
      });
      setDeleteRow(null);
      setOpenDeleteRoomType(false);
      resetLink();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeleteRoomType(false);
    }
  }, [auditResponse]);

  const newLink = () => {
    window.location.href = "/dashboard/roomtypes/new";
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  return (
    <ListPageShell>
      <ListPageCard>
        {(roomTypesResponse.loading || linkResponse.loading) && <Loader />}
        {!roomTypesResponse.loading && !linkResponse.loading && (
          <div>
            <ListPageHeader
              title="Linked Room Types"
              actions={
                authState.role == "Executive" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={newLink}
                  >
                    Create New Link
                  </Button>
                ) : null
              }
            />
            <DataTable
              pagination
              paginationPerPage={5}
              paginationRowsPerPageOptions={[5]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        sx={modalSx}
        open={openDeleteRoomType}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && auditResponse.loading) return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus={true}
      >
        <Fade in={openDeleteRoomType}>
          <Box>
            <DeleteModalFrame title="Delete Link">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="inherit"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default RoomTypes;
